import AddIcon from "@mui/icons-material/AddRounded";
import { Button, Typography } from "@mui/material";
import type {
  GridCellParams,
  GridColDef,
  GridEventListener,
  GridValueFormatter,
  GridValueGetter,
  GridValueSetter,
} from "@mui/x-data-grid-premium";
import { parseISO } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import type { ObjectMetricExtension } from "../../../../api/dataCollectionTypes";
import type {
  ExtensionBasedBlockRow,
  ExtensionRowCellValue,
  MetricAndScenarioTableBlockColumn,
} from "../../../../api/inputFormTypes";
import type {
  MetricDataType,
  MetricExtensionConfiguration,
  MetricFormatConfiguration,
} from "../../../../api/portfolioMonitoringTypes";
import useDefinedContext from "../../../../hooks/useDefinedContext";
import useSingleClickGridEditing from "../../../../hooks/useSingleClickGridEditing";
import { formatDateOnly } from "../../../../utilities/dateUtils";
import { makeNextEditableCellEdited } from "../../../../utilities/editableDataGridHelper";
import { formatMetricNumberValue } from "../../../../utilities/metricFormatters";
import { defined } from "../../../../utilities/typeHelper";
import DataGrid from "../../../grid/DataGrid";
import { getSubmissionDataGridFormStyles } from "../submissionTableFormStyles";
import useDataSubmissionFormContext from "../useDataSubmissionFormContext";
import AddMetricExtensionDialog from "./AddMetricExtensionDialog";
import type { InputFormGridCellValueType } from "./inputFormDataGridHelper";
import { metricDataTypeToGridColTypeMap } from "./inputFormDataGridHelper";
import MetricAndScenarioColumnHeader from "./MetricAndScenarioColumnHeader";
import NewMetricExtensionRowTag from "./NewMetricExtensionRowTag";
import PortfolioMonitoringInputTableContext, {
  PortfolioMonitoringInputTableContextProvider,
} from "./PortfolioMonitoringInputTableContext";
import { calculateTotals } from "./tableTotalsCalculator";

type RowModel = ExtensionBasedBlockRow & {
  isNew?: boolean;
};

interface NameHeaderProps {
  extensionConfiguration: MetricExtensionConfiguration;
  allMetricIds: string[];
}

const NameHeader = ({ extensionConfiguration, allMetricIds }: NameHeaderProps) => {
  const { isSubmissionEditable } = useDataSubmissionFormContext();
  const { onAddMetricExtensions } = useDefinedContext(PortfolioMonitoringInputTableContext);

  return (
    <>
      <Typography variant="subtitle2" noWrap sx={{ flexGrow: 1 }}>
        {extensionConfiguration.extensionName}
      </Typography>
      {isSubmissionEditable && (
        <Button
          variant="text"
          startIcon={<AddIcon />}
          tabIndex={-1}
          onClick={() => onAddMetricExtensions(allMetricIds, "")}
        >
          Add
        </Button>
      )}
    </>
  );
};

const NameCell = ({ row }: { row: RowModel }) => {
  if (row.type === "ExtensionOnly") {
    return (
      <>
        <Typography noWrap sx={{ pl: row.indentationLevel * 2, flexGrow: 1 }}>
          {row.extensionValue}
        </Typography>
        {row.isNew && <NewMetricExtensionRowTag />}
      </>
    );
  }

  if (row.type === "Total") {
    return (
      <Typography variant="subtitle2" noWrap sx={{ pl: row.indentationLevel * 2 }}>
        {row.name}
      </Typography>
    );
  }

  return null;
};

const createNumericValueFormatter =
  (formatConfig: MetricFormatConfiguration | undefined): GridValueFormatter<RowModel> =>
  (value: number | null | undefined, row: RowModel) => {
    if (row.type === "Total") {
      return !value ? "-" : formatMetricNumberValue(value, { ...formatConfig, numberOfDecimals: 0 });
    }

    return typeof value === "number" ? formatMetricNumberValue(value, formatConfig) : "";
  };

const valueGetter: GridValueGetter<RowModel, InputFormGridCellValueType> = (_, row, col) => {
  if (row.type === "Total") {
    return row.values[col.field];
  }

  if (row.type === "ExtensionOnly") {
    return row.values[col.field]?.displayValue;
  }

  return undefined;
};

// MUI Select expects "" (not undefined) as the empty value; undefined triggers its dev-only out-of-range warning.
const selectValueGetter: GridValueGetter<RowModel, InputFormGridCellValueType> = (value, row, col, apiRef) =>
  valueGetter(value, row, col, apiRef) ?? "";

const dateValueGetter: GridValueGetter<RowModel, InputFormGridCellValueType> = (_, row, col) => {
  if (row.type === "Total") {
    return row.values[col.field];
  }

  if (row.type === "ExtensionOnly") {
    const dateIso = row.values[col.field]?.displayValue;
    return typeof dateIso === "string" ? parseISO(dateIso) : undefined;
  }

  return undefined;
};

const valueSetter: GridValueSetter<RowModel, InputFormGridCellValueType> = (newValue, row, col) => {
  if (row.type === "ExtensionOnly") {
    const displayValue = newValue instanceof Date ? formatDateOnly(newValue) : (newValue ?? undefined);

    return {
      ...row,
      values: {
        ...row.values,
        [col.field]: {
          ...row.values[col.field],
          extensionId: row.values[col.field]?.extensionId ?? "",
          displayValue,
        },
      },
    };
  }

  return row;
};

// Map "" back to undefined so closing the select editor without picking a value is not reported as an edit.
const selectValueSetter: GridValueSetter<RowModel, InputFormGridCellValueType> = (newValue, row, col, apiRef) =>
  valueSetter(newValue === "" ? undefined : newValue, row, col, apiRef);

const getValueGetterForType = (dataType: MetricDataType): GridValueGetter<RowModel, InputFormGridCellValueType> => {
  if (dataType === "Date") {
    return dateValueGetter;
  }
  if (dataType === "Select") {
    return selectValueGetter;
  }
  return valueGetter;
};

const editableCellClassName = ({ row }: { row: RowModel }) => (row.type === "Total" ? "total-cell" : "");

const readonlyCellClassName = ({ row }: { row: RowModel }) => (row.type === "Total" ? "total-cell" : "readonly-cell");

const mapScenarioColumn = (
  columnDef: MetricAndScenarioTableBlockColumn,
  isSubmissionEditable: boolean
): GridColDef<RowModel> => ({
  field: columnDef.id,
  flex: 1,
  minWidth: 160,
  type: metricDataTypeToGridColTypeMap[columnDef.dataType],
  editable: isSubmissionEditable && columnDef.isEditable,
  renderHeader: () => <MetricAndScenarioColumnHeader columnDefinition={columnDef} />,
  headerClassName: columnDef.isEditable ? "" : "readonly-header",
  cellClassName: columnDef.isEditable ? editableCellClassName : readonlyCellClassName,
  valueOptions: columnDef.valueSource?.allowedValues,
  valueGetter: getValueGetterForType(columnDef.dataType),
  valueSetter: columnDef.dataType === "Select" ? selectValueSetter : valueSetter,
  valueFormatter:
    columnDef.dataType === "Number" || columnDef.dataType === "Money"
      ? createNumericValueFormatter(columnDef.defaultFormat)
      : undefined,
});

const getColumns = (
  columnDefinitions: MetricAndScenarioTableBlockColumn[],
  extensionConfiguration: MetricExtensionConfiguration,
  isSubmissionEditable: boolean
): GridColDef<RowModel>[] => {
  const allMetricIds = columnDefinitions.map((col) => col.metricId);

  const nameColumn: GridColDef<RowModel> = {
    field: "name",
    headerName: "Metric",
    flex: 2,
    minWidth: 300,
    editable: false,
    cellClassName: ({ row }) => (row.type === "Total" ? "total-cell" : ""),
    renderHeader: () => <NameHeader allMetricIds={allMetricIds} extensionConfiguration={extensionConfiguration} />,
    renderCell: ({ row }) => <NameCell row={row} />,
  };

  const scenarioColumns: GridColDef<RowModel>[] = columnDefinitions.map((columnDef) =>
    mapScenarioColumn(columnDef, isSubmissionEditable)
  );

  return [nameColumn, ...scenarioColumns];
};

const getRows = (rows: ExtensionBasedBlockRow[]): RowModel[] => {
  return calculateTotals(rows);
};

interface Props {
  blockId: string;
  extensionConfiguration: MetricExtensionConfiguration;
  columnDefinitions: MetricAndScenarioTableBlockColumn[];
  rowDefinitions: ExtensionBasedBlockRow[];
}

interface AddExtensionDialogState {
  metricIds: string[];
  metricName: string;
  addedExtensionIds: string[];
}

const QuarterlyDebtInputForm = ({ blockId, extensionConfiguration, columnDefinitions, rowDefinitions }: Props) => {
  const { isSubmissionEditable, onBlockCellValuesEdit } = useDataSubmissionFormContext();

  const { cellModesModel, setCellModesModel, handleCellClick } = useSingleClickGridEditing();

  const [rows, setRows] = useState<RowModel[]>(() => getRows(rowDefinitions));
  const [dialogState, setDialogState] = useState<AddExtensionDialogState>();

  const columns = useMemo(
    () => getColumns(columnDefinitions, extensionConfiguration, isSubmissionEditable),
    [columnDefinitions, extensionConfiguration, isSubmissionEditable]
  );

  const isCellEditable = useCallback(({ row }: { row: RowModel }) => row.type === "ExtensionOnly", []);

  const handleRowUpdate = useCallback(
    (newRow: RowModel, oldRow: RowModel) => {
      if (newRow.type !== "ExtensionOnly" || oldRow.type !== "ExtensionOnly") {
        return newRow;
      }

      const updatedRow = { ...newRow };

      const updatedValueEntry = Object.entries(updatedRow.values).find(
        ([columnId, currentExtValue]) => oldRow.values[columnId]?.displayValue !== currentExtValue.displayValue
      );

      if (updatedValueEntry !== undefined) {
        const [columnId, newExtValue] = updatedValueEntry;
        const colDef = columnDefinitions.find((col) => col.id === columnId);
        if (colDef !== undefined) {
          const metricId = colDef.metricId;
          const metricExtensionId = newExtValue.extensionId;
          const fallbackValue = colDef.dataType === "Money" || colDef.dataType === "Number" ? 0 : "";
          const value = newExtValue.displayValue ?? fallbackValue;
          onBlockCellValuesEdit(blockId, [{ metricId, metricExtensionId, columnId, valueUserInput: value }]);
        }
      }

      setTimeout(
        () =>
          setRows((prevRows) => {
            const newRows = prevRows.map((row) => {
              if (row.id !== updatedRow.id) {
                return row;
              }

              return { ...updatedRow };
            });

            return calculateTotals(newRows);
          }),
        100
      );

      return updatedRow;
    },
    [blockId, columnDefinitions, onBlockCellValuesEdit]
  );

  const handleAddMetricExtensions = useCallback(
    (metricIds: string[], metricName: string) => {
      const addedExtensionIds = rows.flatMap((r) => {
        if (r.type === "ExtensionOnly") {
          const allExtValues = Object.values(r.values);
          return allExtValues.map((v) => v.extensionId);
        }

        return [];
      });

      setDialogState({
        metricIds,
        metricName,
        addedExtensionIds,
      });
    },
    [rows]
  );

  const handleMetricExtensionsAdded = useCallback(
    (addedExtensions: ObjectMetricExtension[], isNew: boolean) => {
      setDialogState(undefined);

      if (addedExtensions.length === 0) {
        return;
      }

      const extensionValue = defined(addedExtensions[0]).value;

      const cellValues = columnDefinitions
        .filter((colDef) => colDef.isEditable && ["Money", "Number", "Text"].includes(colDef.dataType))
        .map((colDef) => {
          const extension = addedExtensions.find((ext) => ext.metricId === colDef.metricId);
          if (extension === undefined) {
            return undefined;
          }

          const emptyValue = colDef.dataType === "Money" || colDef.dataType === "Number" ? 0 : "";
          return [colDef.id, { extensionId: extension.id, displayValue: emptyValue }, colDef.metricId];
        })
        .filter(Boolean) as Array<[string, ExtensionRowCellValue, string]>;

      setRows((prevRows) => {
        const newRow: RowModel = {
          type: "ExtensionOnly",
          isNew,
          id: extensionValue,
          indentationLevel: 0,
          extensionValue,
          values: Object.fromEntries(cellValues) as Record<string, ExtensionRowCellValue>,
        };

        // insert new row before the total row
        const totalRowIndex = prevRows.findIndex((r) => r.type === "Total");
        return [...prevRows.slice(0, totalRowIndex), newRow, ...prevRows.slice(totalRowIndex)];
      });

      onBlockCellValuesEdit(
        blockId,
        cellValues.map(([columnId, extensionCellValue, metricId]) => ({
          metricId,
          metricExtensionId: extensionCellValue.extensionId,
          columnId,
          valueUserInput: extensionCellValue.displayValue ?? 0,
        }))
      );
    },
    [blockId, columnDefinitions, onBlockCellValuesEdit]
  );

  const handleCellKeyDown: GridEventListener<"cellKeyDown"> = useCallback(
    (params: GridCellParams<RowModel>, event) => {
      if (event.code !== "Tab") {
        return;
      }

      event.defaultMuiPrevented = true;
      event.preventDefault();
      const direction = event.shiftKey ? "previous" : "next";
      makeNextEditableCellEdited(direction, rows, columns, params, setCellModesModel, isCellEditable, (r) => r.id);
    },
    [columns, isCellEditable, rows, setCellModesModel]
  );

  return (
    <PortfolioMonitoringInputTableContextProvider onAddMetricExtensions={handleAddMetricExtensions}>
      <DataGrid<RowModel>
        columns={columns}
        rows={rows}
        columnHeaderHeight={110}
        pinnedColumns={{ left: ["name"] }}
        cellModesModel={cellModesModel}
        onCellModesModelChange={setCellModesModel}
        onCellClick={handleCellClick}
        onCellKeyDown={handleCellKeyDown}
        disableColumnReorder
        disableColumnSorting
        showCellVerticalBorder
        showColumnVerticalBorder
        editMode="cell"
        isCellEditable={isCellEditable}
        processRowUpdate={handleRowUpdate}
        noScrollableContainer
        sx={getSubmissionDataGridFormStyles}
      />

      {dialogState && (
        <AddMetricExtensionDialog
          metricIds={dialogState.metricIds}
          metricName={dialogState.metricName}
          addedExtensionIds={dialogState.addedExtensionIds}
          allowCreatingExtensionValues
          onAdded={handleMetricExtensionsAdded}
          onClose={() => setDialogState(undefined)}
        />
      )}
    </PortfolioMonitoringInputTableContextProvider>
  );
};

export default QuarterlyDebtInputForm;
